"use client";

import React, { createContext, useContext, useEffect, useState } from "react";
import { useModelsContext } from "@/providers/models-provider/models-provider";
import { VoiceModel } from "@/actions/db/get-models";
import { convertSessionStorage } from "@/services/storage/convert-session-storage";
import { useConversionEffects } from "@/hooks/ai-tool-process/use-conversion-effects";
import {
  ConvertBaseSettings,
  ConvertBaseContextState,
  useConvertBaseContextProvider,
} from "@/hooks/ai-tool-process/use-convert-base-context-provider";
import { convertBaseInitialSettings } from "@/data/convert-base-initial-settings";
import { convertVoiceLicenseManager } from "@/services/license/license-managers/convert-voice-license-manager";
import { ConvertConfig } from "@/components/pages/convert/hooks/use-start-convert";
import {
  ConvertSettingsField,
  ConvertSettingsStorageMixin,
} from "@/services/storage/storage-mixins/settings-mixins/convert-settings-storage-mixin";

export interface ConvertSettings extends ConvertBaseSettings {
  modelName: string;
  modelId: string;
  f0Method: string;
}

interface ConvertContextState extends ConvertBaseContextState<ConvertSettings> {
  selectedModel?: VoiceModel;
  models: VoiceModel[];
  setRemoveInstrumentals: (value: boolean) => void;
}

const ConvertContext = createContext<ConvertContextState | undefined>(
  undefined,
);

export const useConvertContext = () => {
  if (!ConvertContext) {
    throw new Error("ConvertContext is undefined");
  }

  return useContext(ConvertContext)!;
};

const ConvertProvider: React.FC<{
  children: React.ReactNode;
  model?: VoiceModel;
}> = ({ children, model }) => {
  const initialSettings: ConvertSettings = {
    modelName: model?.ownerId
      ? `${model.ownerId}/${model.name}`
      : model?.name ?? "undefined",
    modelId: model?.id || "",
    f0Method: model?.f0Method || "",
    ...convertBaseInitialSettings,
  };

  const { models } = useModelsContext();

  const [settings, setSettings] = useState<ConvertSettings>(initialSettings);

  const conversionEffects = useConversionEffects({ settings, setSettings });

  const baseContext = useConvertBaseContextProvider<
    ConvertSettings,
    ConvertConfig,
    ConvertSettingsField,
    ConvertSettingsStorageMixin
  >(setSettings, convertSessionStorage);

  useEffect(() => {
    if (model) {
      setSettings((prevState) => ({
        ...prevState,
        modelId: model.id,
      }));
    }
  }, [model]);

  useEffect(() => {
    convertSessionStorage.setDataFromStorage(setSettings).then();
  }, []);

  return (
    <ConvertContext.Provider
      value={{
        settings,
        models,
        licenseManager: convertVoiceLicenseManager,
        selectedModel: model,
        ...baseContext,
        ...conversionEffects,
      }}
    >
      {children}
    </ConvertContext.Provider>
  );
};

export default ConvertProvider;
